// Activity detector used for presence heartbeats.
// Tracks active/idle/background and exposes subscribe + cleanup hooks.

const DEFAULT_IDLE_MS = 180_000; // 3 minutes

export function createActivityTracker({ idleMs = DEFAULT_IDLE_MS } = {}) {
  let activityType = 'active'; // 'active' | 'idle' | 'background'
  let lastActiveAt = Date.now();
  const subscribers = new Set();
  let idleTimer;

  const notify = () => {
    subscribers.forEach((fn) => {
      try {
        fn(activityType, lastActiveAt);
      } catch {
        /* ignore */
      }
    });
  };

  const setActivity = (next) => {
    if (activityType === next) return;
    activityType = next;
    notify();
  };

  const resetIdleTimer = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      if (document.visibilityState === 'hidden') {
        setActivity('background');
      } else {
        setActivity('idle');
      }
    }, idleMs);
  };

  const markActive = () => {
    lastActiveAt = Date.now();
    if (document.visibilityState === 'hidden') {
      setActivity('background');
    } else {
      setActivity('active');
    }
    resetIdleTimer();
  };

  const handleVisibility = () => {
    if (document.visibilityState === 'hidden') {
      setActivity('background');
    } else {
      markActive();
    }
  };

  const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
  events.forEach((ev) =>
    window.addEventListener(ev, markActive, { passive: true }),
  );
  document.addEventListener('visibilitychange', handleVisibility);
  resetIdleTimer();

  return {
    getActivity: () => ({ activityType, lastActiveAt }),
    subscribe(fn) {
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    },
    stop() {
      clearTimeout(idleTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
      events.forEach((ev) => window.removeEventListener(ev, markActive));
      subscribers.clear();
    },
  };
}

export default createActivityTracker;
